<script lang="ts">
  import { link, location } from 'svelte-spa-router';
  import { onMount, onDestroy } from 'svelte';
  import { manifest } from '$stores/content.js';
  import { sidebarOpen } from '$stores/ui.js';
  import { requestPdfExport } from '$stores/pdf.js';

  // Track expanded directories
  let expanded: Set<string> = new Set();

  // Current active path
  $: currentPath = $location.replace(/^\//, '');

  // Auto-expand parent directories when navigating.
  // Uses a store subscription instead of a $: block so that manual toggleExpanded()
  // calls don't re-trigger auto-expansion (the $: block would read `expanded` via
  // .add(), making it a reactive dependency that fires on every toggle).
  let unsubLocation: () => void;
  onMount(() => {
    unsubLocation = location.subscribe(loc => {
      const path = loc.replace(/^\//, '');
      const prefixes = ['volumes/', 'wiki/'];
      for (const prefix of prefixes) {
        if (path.startsWith(prefix)) {
          const parts = path.replace(prefix, '').split('/');
          let current = '';
          for (let i = 0; i < parts.length - 1; i++) {
            current = current ? `${current}/${parts[i]}` : parts[i];
            expanded.add(`${prefix}${current}`);
          }
        }
      }
      expanded = expanded; // Trigger reactivity
    });
  });
  onDestroy(() => unsubLocation?.());

  function toggleExpanded(path: string) {
    if (expanded.has(path)) {
      expanded.delete(path);
    } else {
      expanded.add(path);
    }
    expanded = expanded; // Trigger reactivity
  }

  function closeSidebar() {
    sidebarOpen.set(false);
  }

  // Called on nav link clicks — only closes on mobile where sidebar overlays content
  function closeSidebarOnNav() {
    if (window.innerWidth <= 768) {
      sidebarOpen.set(false);
    }
  }

  interface TreeNode {
    name: string;
    path: string;
    item?: any;
    children: TreeNode[];
  }

  // Build proper tree structure from flat manifest items
  // prefix is used for expanded-state keys (e.g. "volumes/", "wiki/")
  function buildTree(items: any[], prefix: string): TreeNode[] {
    const nodes: Map<string, TreeNode> = new Map();
    const roots: TreeNode[] = [];

    // First pass: collect all unique directory paths
    const dirPaths = new Set<string>();
    for (const item of items) {
      const parts = item.path.split('/');
      let current = '';
      for (let i = 0; i < parts.length - 1; i++) {
        current = current ? `${current}/${parts[i]}` : parts[i];
        dirPaths.add(current);
      }
    }

    // Create directory nodes
    for (const dirPath of Array.from(dirPaths).sort()) {
      const name = dirPath.split('/').pop() || dirPath;
      const displayName = name.replace(/^\d+-/, '').replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      nodes.set(dirPath, {
        name: displayName,
        path: dirPath,
        children: [],
      });
    }

    // Create article nodes
    for (const item of items) {
      nodes.set(item.path, {
        name: item.title,
        path: item.path,
        item,
        children: [],
      });
    }

    // Build hierarchy
    for (const [path, node] of nodes) {
      const parts = path.split('/');
      if (parts.length === 1) {
        roots.push(node);
      } else {
        const parentPath = parts.slice(0, -1).join('/');
        const parent = nodes.get(parentPath);
        if (parent) {
          parent.children.push(node);
        } else {
          roots.push(node);
        }
      }
    }

    // Sort children at each level
    function sortChildren(nodes: TreeNode[]): TreeNode[] {
      return nodes.sort((a, b) => {
        // Directories (no item) come first
        const aIsDir = !a.item;
        const bIsDir = !b.item;
        if (aIsDir !== bIsDir) return aIsDir ? -1 : 1;

        // Extract numeric prefix for sorting
        const aNum = parseInt(a.path.split('/').pop()?.match(/^(\d+)/)?.[1] || '1000');
        const bNum = parseInt(b.path.split('/').pop()?.match(/^(\d+)/)?.[1] || '1000');
        if (aNum !== bNum) return aNum - bNum;

        return a.name.localeCompare(b.name);
      }).map(node => ({
        ...node,
        children: sortChildren(node.children),
      }));
    }

    return sortChildren(roots);
  }

  $: volumeTree = $manifest ? buildTree($manifest.volumes, 'volumes/') : [];
  $: wikiTree = $manifest ? buildTree($manifest.wiki, 'wiki/') : [];

  // Returns the path of the first non-glossary, non-wiki article inside a volume tree node
  function firstArticle(node: TreeNode): string | null {
    for (const child of node.children) {
      const segment = child.path.split('/').pop() ?? '';
      if (segment === 'glossary' || segment === 'wiki') continue;
      if (child.item) return child.path;
      const nested = firstArticle(child);
      if (nested) return nested;
    }
    return null;
  }
</script>

<aside class="sidebar" class:open={$sidebarOpen} aria-label="Document navigation">
  <div class="sidebar-header">
    <h2>{__STRINGS__.sidebar.volumes}</h2>
    <button class="close-btn" on:click={closeSidebar} aria-label={__STRINGS__.sidebar.closeSidebar}>
      &times;
    </button>
  </div>

  <nav class="sidebar-nav" aria-label="Volume navigation">
    {#each volumeTree as node}
      {#if node.children.length > 0}
        <div class="tree-item directory" class:expanded={expanded.has('volumes/' + node.path)}>
          <div class="tree-header">
            <button
              class="tree-toggle"
              on:click={() => toggleExpanded('volumes/' + node.path)}
              aria-expanded={expanded.has('volumes/' + node.path)}
            >
              <span class="icon">{expanded.has('volumes/' + node.path) ? '▼' : '▶'}</span>
              {node.name}
            </button>
            <div class="volume-actions">
              {#if firstArticle(node)}
                <a
                  href="/volumes/{firstArticle(node)}"
                  use:link
                  class="vol-btn"
                  title={__STRINGS__.sidebar.openFirstPage}
                  on:click|stopPropagation={closeSidebarOnNav}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                    <polyline points="10 17 15 12 10 7"/>
                    <line x1="15" y1="12" x2="3" y2="12"/>
                  </svg>
                </a>
              {/if}
              <button
                class="vol-btn"
                title={__STRINGS__.sidebar.exportPdf}
                on:click|stopPropagation={() => requestPdfExport(node.path, node.name)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14 2 14 8 20 8"/>
                  <line x1="12" y1="18" x2="12" y2="12"/>
                  <polyline points="9 15 12 18 15 15"/>
                </svg>
              </button>
            </div>
          </div>

          {#if expanded.has('volumes/' + node.path)}
            <div class="tree-children">
              {#each node.children as child}
                {#if child.item}
                  <a
                    href="/volumes/{child.path}"
                    use:link
                    class="tree-link"
                    class:active={currentPath === `volumes/${child.path}`}
                    on:click={closeSidebarOnNav}
                  >
                    {child.name}
                  </a>
                {:else}
                  <!-- Nested directory -->
                  <div class="tree-item directory nested" class:expanded={expanded.has('volumes/' + child.path)}>
                    <button
                      class="tree-toggle"
                      on:click={() => toggleExpanded('volumes/' + child.path)}
                      aria-expanded={expanded.has('volumes/' + child.path)}
                    >
                      <span class="icon">{expanded.has('volumes/' + child.path) ? '▼' : '▶'}</span>
                      {child.name}
                    </button>
                    {#if expanded.has('volumes/' + child.path)}
                      <div class="tree-children">
                        {#each child.children as grandchild}
                          <a
                            href="/volumes/{grandchild.path}"
                            use:link
                            class="tree-link"
                            class:active={currentPath === `volumes/${grandchild.path}`}
                            on:click={closeSidebarOnNav}
                          >
                            {grandchild.name}
                          </a>
                        {/each}
                      </div>
                    {/if}
                  </div>
                {/if}
              {/each}
            </div>
          {/if}
        </div>
      {:else if node.item}
        <a
          href="/volumes/{node.path}"
          use:link
          class="tree-link root-link"
          class:active={currentPath === `volumes/${node.path}`}
          on:click={closeSidebarOnNav}
        >
          {node.name}
        </a>
      {/if}
    {/each}
  </nav>

  {#if $manifest && $manifest.wiki.length > 0}
    <div class="sidebar-section">
      <h3>{__STRINGS__.sidebar.wiki}</h3>
      <nav class="wiki-nav" aria-label="Wiki navigation">
        {#each wikiTree as node}
          {#if node.children.length > 0}
            <div class="tree-item directory" class:expanded={expanded.has('wiki/' + node.path)}>
              <button
                class="tree-toggle"
                on:click={() => toggleExpanded('wiki/' + node.path)}
                aria-expanded={expanded.has('wiki/' + node.path)}
              >
                <span class="icon">{expanded.has('wiki/' + node.path) ? '▼' : '▶'}</span>
                {node.name}
              </button>

              {#if expanded.has('wiki/' + node.path)}
                <div class="tree-children">
                  {#each node.children as child}
                    {#if child.item}
                      <a
                        href="/wiki/{child.path}"
                        use:link
                        class="tree-link"
                        class:active={currentPath === `wiki/${child.path}`}
                        on:click={closeSidebarOnNav}
                      >
                        {child.name}
                      </a>
                    {:else}
                      <!-- Nested directory -->
                      <div class="tree-item directory nested" class:expanded={expanded.has('wiki/' + child.path)}>
                        <button
                          class="tree-toggle"
                          on:click={() => toggleExpanded('wiki/' + child.path)}
                          aria-expanded={expanded.has('wiki/' + child.path)}
                        >
                          <span class="icon">{expanded.has('wiki/' + child.path) ? '▼' : '▶'}</span>
                          {child.name}
                        </button>
                        {#if expanded.has('wiki/' + child.path)}
                          <div class="tree-children">
                            {#each child.children as grandchild}
                              <a
                                href="/wiki/{grandchild.path}"
                                use:link
                                class="tree-link"
                                class:active={currentPath === `wiki/${grandchild.path}`}
                                on:click={closeSidebarOnNav}
                              >
                                {grandchild.name}
                              </a>
                            {/each}
                          </div>
                        {/if}
                      </div>
                    {/if}
                  {/each}
                </div>
              {/if}
            </div>
          {:else if node.item}
            <a
              href="/wiki/{node.path}"
              use:link
              class="tree-link"
              class:active={currentPath === `wiki/${node.path}`}
              on:click={closeSidebarOnNav}
            >
              {node.name}
            </a>
          {/if}
        {/each}
      </nav>
    </div>
  {/if}
</aside>

<style>
  .sidebar {
    width: 280px;
    height: calc(100vh - 56px);
    background: var(--color-sidebar-bg, #f5f5f5);
    border-right: 1px solid var(--color-border, #e0e0e0);
    overflow-y: auto;
    flex-shrink: 0;
    position: fixed;
    top: 56px;
    left: -280px;
    z-index: 1;
    transition: left 0.2s ease-out;
  }

  .sidebar.open {
    left: 0;
  }

  .sidebar-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 1rem;
    border-bottom: 1px solid var(--color-border, #e0e0e0);
  }

  .sidebar-header h2 {
    margin: 0;
    font-size: 1rem;
    font-weight: 600;
    color: var(--color-heading, #1a1a2e);
  }

  .close-btn {
    display: none;
    background: none;
    border: none;
    font-size: 1.5rem;
    cursor: pointer;
    color: var(--color-text-muted, #666);
  }

  .sidebar-nav {
    padding: 0.5rem;
  }

  .sidebar-section {
    padding: 1rem;
    border-top: 1px solid var(--color-border, #e0e0e0);
  }

  .sidebar-section h3 {
    margin: 0 0 0.5rem;
    font-size: 0.875rem;
    font-weight: 600;
    color: var(--color-text-muted, #666);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .wiki-nav {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }

  .tree-item {
    margin-bottom: 2px;
  }

  .tree-header {
    display: flex;
    align-items: center;
  }

  .volume-actions {
    display: flex;
    align-items: center;
    gap: 1px;
    padding-right: 4px;
    flex-shrink: 0;
  }

  .vol-btn {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    padding: 0;
    background: none;
    border: none;
    border-radius: 4px;
    color: var(--color-text-muted, #666);
    cursor: pointer;
    text-decoration: none;
    flex-shrink: 0;
  }

  .vol-btn:hover:not([disabled]) {
    background: var(--color-sidebar-hover, rgba(0, 0, 0, 0.08));
    color: var(--color-text, #333);
  }

  .tree-toggle {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex: 1;
    min-width: 0;
    padding: 0.5rem;
    background: none;
    border: none;
    font-size: 0.9rem;
    font-weight: 500;
    color: var(--color-text, #333);
    text-align: left;
    cursor: pointer;
    border-radius: 4px;
  }

  .tree-toggle:hover {
    background: var(--color-sidebar-hover, rgba(0, 0, 0, 0.05));
  }

  .tree-toggle .icon {
    font-size: 0.6rem;
    width: 1em;
    opacity: 0.6;
  }

  .tree-children {
    padding-left: 1rem;
    margin-top: 2px;
  }

  .nested .tree-children {
    padding-left: 1rem;
  }

  .tree-link {
    display: block;
    padding: 0.4rem 0.5rem;
    color: var(--color-text, #333);
    text-decoration: none;
    font-size: 0.9rem;
    border-radius: 4px;
  }

  .tree-link:hover {
    background: var(--color-sidebar-hover, rgba(0, 0, 0, 0.05));
  }

  .tree-link.active {
    background: var(--color-primary, #e94560);
    color: white;
  }

  .root-link {
    font-weight: 500;
  }

  /* Match the overlay's breakpoint so the sidebar is always above the dim backdrop */
  @media (max-width: 1200px) {
    .sidebar {
      z-index: 99;
    }
  }

  @media (max-width: 768px) {
    .close-btn {
      display: block;
    }
  }
</style>
